import { useState } from "react";
import { Check, Pencil } from "lucide-react";

const SEASONS = ["SS", "FW", "Resort", "Pre-Fall", "Continuativa", "Altro"];
const UNITS = [["m", "metri"], ["kg", "kg"], ["unit", "pezzi"]];

function Radio({ q, draft, setDraft }) {
  return (
    <div className="space-y-2">
      {q.options.map(o => (
        <label key={o.value} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`q-opt-${q.id}-${o.value}`}>
          <input type="radio" className="mt-1" checked={draft.value === o.value} onChange={() => setDraft({ ...draft, value: o.value })}/>
          <span>{o.label}</span>
        </label>))}
    </div>
  );
}

function MaterialFields({ draft, setDraft, suppliers, materials }) {
  const set = (p) => setDraft({ ...draft, ...p });
  if (draft.value === "map") return (
    <select className="input-dark mt-3" value={draft.material_id || ""} onChange={e => set({ material_id: e.target.value })} data-testid="q-material-map">
      <option value="">Scegli materiale…</option>{materials.map(m => <option key={m.id} value={m.id}>{m.name}{m.code ? ` · ${m.code}` : ""}</option>)}
    </select>);
  if (draft.value !== "create") return null;
  return (
    <div className="grid md:grid-cols-4 gap-3 mt-3" data-testid="q-material-create">
      <input className="input-dark md:col-span-2" placeholder="Nome materiale" value={draft.name ?? ""} onChange={e => set({ name: e.target.value })} data-testid="q-material-name"/>
      <select className="input-dark" value={draft.unit || "m"} onChange={e => set({ unit: e.target.value })} data-testid="q-material-unit">
        {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <input type="number" step="0.01" className="input-dark" placeholder="Costo €" value={draft.cost_per_unit ?? ""} onChange={e => set({ cost_per_unit: e.target.value })} data-testid="q-material-cost"/>
      <select className="input-dark md:col-span-2" value={draft.supplier_id || (draft.new_supplier_name ? "__new__" : "")}
        onChange={e => set(e.target.value === "__new__" ? { supplier_id: null, new_supplier_name: draft.new_supplier_name || "" } : { supplier_id: e.target.value || null, new_supplier_name: null })} data-testid="q-material-supplier">
        <option value="">Nessun fornitore</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}<option value="__new__">+ Nuovo fornitore…</option>
      </select>
      {!draft.supplier_id && draft.new_supplier_name !== null && draft.new_supplier_name !== undefined &&
        <input className="input-dark md:col-span-2" placeholder="Nome nuovo fornitore" value={draft.new_supplier_name} onChange={e => set({ new_supplier_name: e.target.value })} data-testid="q-material-new-supplier"/>}
    </div>
  );
}

function CollectionFields({ draft, setDraft }) {
  if (draft.value !== "new") return null;
  const set = (p) => setDraft({ ...draft, ...p });
  return (
    <div className="grid md:grid-cols-3 gap-3 mt-3" data-testid="q-collection-new">
      <input className="input-dark" placeholder="Nome collezione" value={draft.name || ""} onChange={e => set({ name: e.target.value })} data-testid="q-collection-name"/>
      <select className="input-dark" value={draft.season || ""} onChange={e => set({ season: e.target.value || null })} data-testid="q-collection-season">
        <option value="">Stagione…</option>{SEASONS.map(s => <option key={s}>{s}</option>)}</select>
      <input type="number" className="input-dark" placeholder="Anno" value={draft.year || ""} onChange={e => set({ year: e.target.value })} data-testid="q-collection-year"/>
    </div>
  );
}

const needsText = (q, d) => (d.value === "text") || (q.kind === "generic" && d.value === "single");

export function QuestionCard({ q, onAnswer, suppliers, materials, busy }) {
  const [editing, setEditing] = useState(!q.answered);
  const [draft, setDraft] = useState(q.answer || q.default || { value: q.kind === "text" ? "text" : undefined });
  const valid = draft.value && (!needsText(q, draft) || (draft.text || "").trim() || q.kind === "generic")
    && !(draft.value === "map" && !draft.material_id) && !(q.kind === "collection" && draft.value === "new" && !(draft.name || "").trim());
  if (!editing) return (
    <div className="card-dark p-4 flex items-start gap-3" data-testid={`question-${q.id}`}>
      <Check size={16} className="text-green-700 mt-0.5 shrink-0"/>
      <div className="flex-1 text-sm"><div className="font-medium">{q.title}</div>
        <div className="opacity-70 mt-1" data-testid={`question-answer-${q.id}`}>{q.options.find(o => o.value === q.answer?.value)?.label || q.answer?.value}{q.answer?.text ? `: ${q.answer.text}` : ""}{q.answer?.name ? `: ${q.answer.name}` : ""}</div></div>
      <button onClick={() => setEditing(true)} className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid={`question-edit-${q.id}`}><Pencil size={12}/> Modifica</button>
    </div>);
  return (
    <div className="card-dark p-5" style={{ borderLeft: "3px solid #b45309" }} data-testid={`question-${q.id}`}>
      <div className="font-medium mb-1">{q.title}</div>
      {q.detail && <div className="text-sm opacity-70 mb-4">{q.detail}</div>}
      {q.kind === "collection" ? (
        <select className="input-dark" value={draft.value || ""} onChange={e => setDraft({ ...draft, value: e.target.value })} data-testid={`q-select-${q.id}`}>
          <option value="">Scegli…</option>{q.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
      ) : q.kind === "text" ? null : <Radio q={q} draft={draft} setDraft={setDraft}/>}
      {needsText(q, draft) && <input className="input-dark mt-3" autoFocus placeholder={q.kind === "generic" ? "Nome del materiale" : q.id.startsWith("name:") ? "Nome del capo" : "Codice"}
        value={draft.text ?? ""} onChange={e => setDraft({ ...draft, text: e.target.value })} data-testid={`q-text-${q.id}`}/>}
      {q.kind === "text" && <button className="text-xs mt-2 underline opacity-70" onClick={() => onAnswer({ value: "skip" })} data-testid={`q-skip-${q.id}`}>Non importare questa riga</button>}
      {q.kind === "material" && <MaterialFields draft={draft} setDraft={setDraft} suppliers={suppliers} materials={materials}/>}
      {q.kind === "collection" && <CollectionFields draft={draft} setDraft={setDraft}/>}
      <div className="flex justify-end gap-2 mt-4">
        {q.answered && <button className="btn-ghost" onClick={() => setEditing(false)}>Annulla</button>}
        <button disabled={!valid || busy} className="btn-gold" onClick={() => { onAnswer(draft); setEditing(false); }} data-testid={`q-confirm-${q.id}`}>Conferma</button>
      </div>
    </div>
  );
}
